import {shallowEqual, useSelector} from "react-redux";
import {RootState} from "../../store";
import {css} from "@emotion/react";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button, Integration, MainLoader, Navigator} from "ui-components";
import {CloseConsoleIcon, ExpandIcon, ShrinkIcon} from "ui-icons/react";
import {useConsole} from "./consoleStore";
import useResizableConsole from "./useResizableConsole";

const serviceCss = css`
  height: 100%;
  background-color: var(--content-background);
`;
const headerCss = css`
  z-index: 17;
  position: absolute;
  right: 0px;
  display: flex;
  padding: 16px 40px 16px 24px;
  align-items: center;
  flex-direction: row-reverse;
`;

const draggerCss = css`
  width: 49px;
  border-radius: 8px 8px 0px 0px;
  height: 100%;
  border: 1px solid var(--icon-round-border, --border);
  background: white;
  border-bottom: none;
  padding: 0px 16px;
  height: 12px;
  display: flex;
  align-items: center;
  margin: 0 auto;
  cursor: ns-resize;
  position: absolute;
  top: -12px;
  left: calc(50% - 24.5px);
  z-index: 17;
`;

interface ComponentState {
    isInitializing: boolean
    isMounted: boolean
    shadowRoot: ShadowRoot | null
    integration: Integration | null
}

export const ConsoleContainer = () => {
    const containerRef = useRef(null);
    const {
        consoleHeight,
        setConsoleHeight,
        setIsConsoleOpen,
        newTerminalWithCredentials,
        isMaximized,
        setIsMaximized
    } = useConsole((state) => state);
    const {startResizing} = useResizableConsole(containerRef);
    const {idTokenPayload: userMetadata, accessToken} = useSelector((state: RootState) => state.auth);
    const {selectedAccountId: accountId, isDemo: isDemoMode} = useSelector((state: RootState) => state.tenancy);
    const {isInitialized: isFeaturesInitialized} = useSelector((state: RootState) => state.features);
    const features = useSelector((state: RootState) => ({
        byKey: state.features.byKey,
        active: state.features.active
    }), shallowEqual);

    const stateRef = useRef<ComponentState>({
        isInitializing: false,
        isMounted: false,
        shadowRoot: null,
        integration: null,
    });
    const consoleWorkloadConfig = useSelector((state: RootState) => state.services.workloadConfigsMap.console);
    const {url} = consoleWorkloadConfig.stageIntegrations[0];
    useEffect(() => {
        if (!stateRef.current.isInitializing && !stateRef.current.isMounted && url) {
            stateRef.current.isInitializing = true;
            (import.meta.env.DEV && window.__localIntegration && window.__localIntegration.id === 'console' ? Promise.resolve(window.__localIntegration) : import(/* @vite-ignore */ url)).then((integration: Integration) => {
                const initializationData = {
                    userMetadata,
                    accessToken,
                    accountId: accountId === "no-project" ? undefined : accountId,
                    isDemoMode: isDemoMode || false,
                    features
                };
                integration?.mount(stateRef.current.shadowRoot as ShadowRoot, initializationData).then(() => {
                    stateRef.current.integration = integration;
                    stateRef.current.isInitializing = false;
                    stateRef.current.isMounted = true;
                    stateRef?.current?.integration?.openNewConsole?.(newTerminalWithCredentials);
                });
            });
        }

        return () => {
            if (stateRef.current.isMounted) {
                stateRef.current.isMounted = false;
                stateRef.current.integration.unmount();
                stateRef.current.integration = null;
            }
        }
    }, []);

    return <div ref={containerRef} style={{position: 'relative'}}>
        <div onMouseDown={startResizing} css={draggerCss}>
            <div style={{borderRadius: 8, height: 3, background: 'var(--icon-tertiary)', width: 17}}></div>
        </div>
        <div css={headerCss}>
            <Button color="secondary" variant="icon" onClick={() => setIsConsoleOpen(false)} ><CloseConsoleIcon/></Button>
            {isMaximized ?  <Button onClick={() => {setIsMaximized(false);setConsoleHeight('324px');}} color="secondary" variant="icon"><ShrinkIcon style={{marginRight: 24}}/></Button> :
                <Button onClick={() => {setIsMaximized(true);setConsoleHeight('100%');}} color="secondary" variant="icon"><ExpandIcon style={{marginRight: 24}}/></Button>}
        </div>
        <div data-stage={'console'} css={serviceCss} style={{overflowY: "auto"}} ref={container => {
            if (container && !stateRef.current.shadowRoot) {
                stateRef.current.shadowRoot = container.attachShadow({mode: "open"});
            }
        }}/>
    </div>
}